import { getIn } from "formik";

const fallbackManagers = [
  {
    id: "pm-1",
    name: "Raed Ahmed Subhi",
    email: "raed@proline.sa",
    phone: "+966 560 090 803",
  },
  {
    id: "pm-2",
    name: "Sami Al-Harbi",
    email: "sami.alharbi@proline.sa",
    phone: "+966 555 010 104",
  },
  {
    id: "pm-3",
    name: "Noura Al-Qahtani",
    email: "noura.qahtani@proline.sa",
    phone: "+966 555 010 208",
  },
];

export const createZone = () => ({
  name: "",
  supervisor_id: "",
  supervisor_name: "",
  applicants_in_zone_count: null,
  manager_id: "",
  manager_name: "",
  manager_email: "",
  manager_phone: "",
  image_url: "",
});

export const createRequirement = () => ({
  requirement_id: "",
  name: "",
  quantity: "",
  notes: "",
});

/** Row with no meaningful input — omitted from submit payload when section is optional. */
export const isEmptyLogisticsRequirementRow = (row = {}) => {
  const rawId = row?.requirement_id;
  const hasId =
    rawId !== "" &&
    rawId != null &&
    rawId !== undefined &&
    !(typeof rawId === "object" && rawId?.id == null);
  const hasName = String(row?.name ?? "").trim() !== "";
  const qty = row?.quantity;
  const hasQty =
    qty !== "" &&
    qty != null &&
    qty !== undefined &&
    Number.isFinite(Number(qty)) &&
    Number(qty) >= 1;
  const hasNotes = String(row?.notes ?? "").trim() !== "";
  const hasPersistedId = row?.id != null && row?.id !== "";
  return !hasId && !hasName && !hasQty && !hasNotes && !hasPersistedId;
};

export const getVisibleError = (formik, name) => {
  const error = getIn(formik.errors, name);
  const touched = getIn(formik.touched, name);
  return touched || formik.submitCount > 0 ? error : "";
};

export const getManagerOptions = (projectManagers) => {
  const source = projectManagers?.length ? projectManagers : fallbackManagers;

  return source.map((manager) => ({
    id: manager.id,
    name: manager.name || manager.full_name || manager.email,
    email: manager.email || "",
    phone: manager.phone || manager.mobile_number || manager.mobile || "",
  }));
};

export const getRequirementOptions = (requirements) =>
  (requirements || []).map((requirement, index) => ({
    id:
      requirement.id ??
      requirement.value ??
      requirement.key ??
      requirement.code ??
      index,
    name:
      requirement.name ||
      requirement.label ||
      String(requirement.id ?? requirement.value ?? ""),
  }));

const normalizeRequirementLabel = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

/** Stable catalog id for a saved requirement row (id or matched name). */
export const resolveRequirementRowSelectionKey = (row, catalogOptions = []) => {
  const rawId = row?.requirement_id;

  if (rawId !== "" && rawId != null) {
    if (typeof rawId === "object") {
      const objectId =
        rawId.id ?? rawId.value ?? rawId.requirement_id ?? rawId.key;
      if (objectId !== "" && objectId != null) {
        return String(objectId);
      }
    } else {
      return String(rawId);
    }
  }

  const rowName = normalizeRequirementLabel(row?.name);
  if (!rowName) return null;

  const catalogMatch = (catalogOptions || []).find(
    (option) =>
      normalizeRequirementLabel(option.name || option.label) === rowName,
  );

  return catalogMatch ? String(catalogMatch.id) : null;
};

/** Options for one row — excludes requirements already picked in other rows. */
export const getAvailableRequirementOptions = (
  allRequirements,
  currentIndex,
  catalogOptions = [],
) => {
  const selectedKeys = new Set(
    (allRequirements || [])
      .map((row, index) => {
        if (index === currentIndex) return null;
        return resolveRequirementRowSelectionKey(row, catalogOptions);
      })
      .filter(Boolean),
  );

  const options = (catalogOptions || []).filter(
    (option) => !selectedKeys.has(String(option.id)),
  );

  const currentKey = resolveRequirementRowSelectionKey(
    allRequirements?.[currentIndex],
    catalogOptions,
  );
  if (!currentKey) return options;

  if (options.some((option) => String(option.id) === currentKey)) {
    return options;
  }

  const currentRow = allRequirements[currentIndex];
  const fromCatalog = (catalogOptions || []).find(
    (option) => String(option.id) === currentKey,
  );
  const name =
    fromCatalog?.name ||
    String(currentRow?.name ?? "").trim() ||
    currentKey;

  return [
    {
      id: fromCatalog?.id ?? currentRow?.requirement_id ?? currentKey,
      name,
    },
    ...options,
  ];
};

export const getSelectedRequirementOption = (
  allRequirements,
  currentIndex,
  catalogOptions = [],
  availableOptions = [],
) => {
  const row = allRequirements?.[currentIndex];
  const selectionKey = resolveRequirementRowSelectionKey(row, catalogOptions);
  if (!selectionKey) return null;

  return (
    availableOptions.find((option) => String(option.id) === selectionKey) ||
    catalogOptions.find((option) => String(option.id) === selectionKey) || {
      id: row?.requirement_id ?? selectionKey,
      name: row?.name || selectionKey,
    }
  );
};

export const buildTouchedZonesAndRequirements = (
  values,
  options = {},
) => {
  const portal = Boolean(options.companyPortalMode);
  return {
    ...(portal ? { zones_image_url: true } : {}),
    zones_attributes: values.zones_attributes.map(() =>
      portal
        ? { name: true, supervisor_id: true }
        : {
            name: true,
            supervisor_name: true,
            manager_id: true,
            manager_name: true,
            manager_email: true,
            manager_phone: true,
          },
    ),
    logistics_requirements_attributes:
      values.logistics_requirements_attributes.map(() => ({
        requirement_id: true,
        name: true,
        quantity: true,
        notes: true,
      })),
  };
};

/**
 * Whether a failed continue attempt should reveal `name`'s error. Zones added
 * after that attempt (index >= validatedZoneCount) are excluded until touched.
 */
export const sectionErrorsApplyTo = (name, showSectionErrors, validatedZoneCount) => {
  if (!showSectionErrors) return false;
  const zoneMatch = /^zones_attributes\.(\d+)/.exec(name);
  return !zoneMatch || Number(zoneMatch[1]) < validatedZoneCount;
};
